/**
 * Croatian catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Croatian at a mathematician's register. **Warrant → «Opravdanje»**
 * (justification — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / few / other, with the noun «ograničenje» declined accordingly
 * (1 ograničenje · 2–4 ograničenja · 5+ ograničenja), so `frameActive` declares exactly those
 * (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a presentation concern
 * only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const HR_CATALOG = {
    locale: 'hr',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Istraživanje',
        [P2.sectionClaims]: 'Tvrdnje',
        [P2.sectionIntegrity]: 'Cjelovitost',
        [P2.axisWarrantLabel]: 'Opravdanje',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirijsko',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definicija} interp {Tumačenje} testable {Provjerljivo} tested {Provjereno} refuted {Pobijeno} program {Istraživački program} philos {Filozofsko} other {Nepoznato}}',
        [P2.axisModalLabel]: 'Modalno',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Razina snage {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Istraživo',
        [P2.stateRefuted]: 'Pobijeno',
        [P2.stateOutside]: 'Izvan opsega',
        [P2.verdictConflict]: 'U sukobu s {constraints}',
        [P2.verdictOutside]: 'Zahtijeva {missing}',
        [P2.lossRegisterPlain]: 'Pojednostavljeno na obični jezik — dio preciznosti je izostavljen.',
        [P2.lossRegisterKid]: 'Pojednostavljeno za mlađe čitatelje — mnogi detalji su izostavljeni.',
        [P2.lossProfileDetail]: 'Cjeloviti profil Opravdanje / Empirijsko / Modalno ovdje je sažet.',
        [P2.lossConflictDetail]: 'Pojedinosti sukoba skraćene su u ovom prikazu.',
        [P2.laneVerified]: 'Provjereno',
        [P2.laneUnverified]: 'Neprovjereno',
        [P2.laneConflict]: 'Sukob dokazan',
        [P2.constraintLogic]: 'Logika',
        [P2.constraintLightBends]: 'Svjetlost se zakrivljuje u gravitacijskom polju',
        [P2.constraintParallelPostulate]: 'Postulat o paralelama',
        [P2.claimScalarGravity]: 'Skalarna teorija gravitacije',
        [P2.claimNonEuclidean]: 'Neeuklidska geometrija',
        [P2.claimSubstrateSr]: 'Supstratna interpretacija specijalne relativnosti',
        [P2.edgeExtObservation]: 'Vanjsko promatranje',
        [P2.edgeResourceSample]: 'Dijeljeni računalni resurs',
        [P2.edgeHeuristicSample]: 'Heuristička smjernica',
        [P2.edgeSharedSample]: 'Zajednički temelj',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ograničenja',
        [P2.sectionEdges]: 'Bridovi',
        [P2.sectionFrame]: 'Okvir',
        [P2.sectionExposure]: 'Izloženost',
        [P2.statusAdoptedFormal]: 'Usvojeno (formalno)',
        [P2.statusAdoptedInterpretive]: 'Usvojeno (interpretativno)',
        [P2.statusSuspended]: 'Suspendirano',
        [P2.edgeEffectConstraint]: 'Tvori ograničenje (može pobiti)',
        [P2.edgeEffectResource]: 'Resurs — bez učinka na presude',
        [P2.edgeEffectHeuristic]: 'Heuristika — bez učinka na presude',
        [P2.edgeEffectShared]: 'Zajednički temelj — bez učinka na presude',
        [P2.frameActive]: '{count, plural, =0 {Nema aktivnih ograničenja} one {# aktivno ograničenje} few {# aktivna ograničenja} other {# aktivnih ograničenja}}',
        [P2.exposureNone]: 'Nema interpretativnih ovisnosti',
        [P2.exposureDep]: 'Ovisi o {id}',
        [P2.laneTrackMachine]: 'Stroj',
        [P2.laneTrackExpert]: 'Stručnjak',
        [P2.laneTrackCommunity]: 'Zajednica',
        [P2.expertAudited]: 'Revidirano',
        [P2.expertInProgress]: 'U tijeku',
        [P2.expertNotReviewed]: 'Nije pregledano',
        [P2.communityFlags]: 'Postoje oznake',
        [P2.communityNone]: 'Nema oznaka',
        [P2.controlChallenge]: 'Ospori',
        [P2.controlActivate]: 'Aktiviraj',
        [P2.controlMakeInterpretive]: 'Učini interpretativnim',
        [P2.controlImportEdge]: 'Uvezi brid',
        [P2.controlPromote]: 'Promakni',
        [P2.controlUndo]: 'Poništi',
        [P2.controlRedo]: 'Ponovi',
        [P2.controlRegister]: 'Registriraj',
        [P2.promoteRejected]: 'Promaknuće odbijeno',
        [P2.promoteAccepted]: 'Promaknuće prihvaćeno',
        [P2.promoteReasonRefutationTerminal]: 'Dokaz ne može izliječiti pobijanje.',
        [P2.promoteReasonKeystone]: 'Ključno promaknuće zahtijeva empirijsku potporu.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empirijska vrsta nije fiksirana.',
        [P2.promoteReasonModalNotFixed]: 'Modalni stupanj nije fiksiran.',
        [P2.promoteReasonWarrantNotImproved]: 'Stupanj opravdanja nije poboljšan.',
        [P2.evidenceComputation]: "{engine} je izračunao {op} = {result} pod {config}",
        [P2.lossNumericRounding]: "Rezultat zaokružen na {dp} decimalnih mjesta",
    },
};
//# sourceMappingURL=hr.js.map